import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../lib/auth-context';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';
import {
  AUTH_ROUTE_CONTEXT_PRESETS,
  enforcePublicRouteIsolation,
  resolveAuthRouteContextFromPreset,
} from '../lib/auth-route-isolation';
import {
  CLOUD_BOOTSTRAP_DONE_KEY,
  ensureRecoveredAccountState,
  ensureSyncEnabledByDefault,
  syncNow,
} from '../lib/cloud-sync';
import { isOnboardingComplete, markOnboardingComplete } from '../lib/onboarding-state';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { syncMfaFromSession } = useAuth();
  const effectGeneration = useRef(0);

  useEffect(() => {
    const id = ++effectGeneration.current;
    let cancelled = false;

    (async () => {
      const queryParams = new URLSearchParams(window.location.search);
      const callbackContext = resolveAuthRouteContextFromPreset(
        queryParams,
        AUTH_ROUTE_CONTEXT_PRESETS.authCallback,
      );

      await new Promise((r) => setTimeout(r, 400));
      if (cancelled || id !== effectGeneration.current) return;

      await enforcePublicRouteIsolation(callbackContext.hasContext);
      if (cancelled || id !== effectGeneration.current) return;

      if (!callbackContext.hasContext) {
        navigate('/auth', { replace: true });
        return;
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (cancelled || id !== effectGeneration.current) return;

      if (!session?.user) {
        toast.error('Authentication failed. Please try again.');
        navigate('/auth', { replace: true });
        return;
      }

      const needMfa = await syncMfaFromSession();
      if (cancelled || id !== effectGeneration.current) return;

      if (needMfa) {
        toast.message('Complete two-factor sign-in');
        navigate('/auth?mfa=1', { replace: true });
        return;
      }

      toast.success('Signed in successfully');

      ensureSyncEnabledByDefault();
      try {
        if (session.access_token) {
          await syncNow(session.access_token, { force: true });
        }
      } catch {
        /* offline / transient — still normalize from metadata below */
      } finally {
        ensureRecoveredAccountState();
        localStorage.setItem(CLOUD_BOOTSTRAP_DONE_KEY, 'true');
      }

      const metadata = session.user?.user_metadata as Record<string, unknown> | undefined;
      const parseMaybeJson = (value: unknown): Record<string, unknown> | null => {
        if (value && typeof value === 'object') return value as Record<string, unknown>;
        if (typeof value === 'string') {
          try {
            const parsed = JSON.parse(value);
            return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
          } catch {
            return null;
          }
        }
        return null;
      };
      const relationshipProfile = parseMaybeJson(metadata?.tension_relationship_profile);
      const userProfile = parseMaybeJson(metadata?.tension_user_profile);
      const partnerName =
        typeof relationshipProfile?.partnerName === 'string'
          ? relationshipProfile.partnerName
          : typeof metadata?.tension_partner_name === 'string'
            ? metadata.tension_partner_name
            : '';
      const relationshipType =
        typeof relationshipProfile?.relationshipType === 'string'
          ? relationshipProfile.relationshipType
          : typeof metadata?.tension_relationship_type === 'string'
            ? metadata.tension_relationship_type
            : '';
      const relationshipStart =
        typeof relationshipProfile?.relationshipStartDate === 'string'
          ? relationshipProfile.relationshipStartDate
          : typeof metadata?.tension_relationship_start_date === 'string'
            ? metadata.tension_relationship_start_date
            : '';
      const onboardingComplete =
        typeof metadata?.tension_onboarding_complete === 'string'
          ? metadata.tension_onboarding_complete
          : '';
      const mode = typeof metadata?.tension_mode === 'string' ? metadata.tension_mode : '';

      if (onboardingComplete === 'true' && !isOnboardingComplete()) {
        markOnboardingComplete();
      }
      if ((mode === 'relationship' || mode === 'single') && !localStorage.getItem('tension_mode')) {
        localStorage.setItem('tension_mode', mode);
      }
      if (!localStorage.getItem('tension_relationships') && partnerName && relationshipType) {
        const relationshipId = `rel_${Date.now()}`;
        const relationship = {
          id: relationshipId,
          name: partnerName,
          type: relationshipType,
          startDate: relationshipStart || new Date().toISOString().split('T')[0],
          createdAt: new Date().toISOString(),
          isActive: true,
        };
        localStorage.setItem('tension_relationships', JSON.stringify([relationship]));
        localStorage.setItem('tension_active_relationship', relationshipId);
        markOnboardingComplete();
        localStorage.setItem('tension_mode', 'relationship');
      }

      if (userProfile && !localStorage.getItem('tension_user_profile')) {
        localStorage.setItem('tension_user_profile', JSON.stringify(userProfile));
      }
      if (relationshipProfile && !localStorage.getItem('tension_relationship_profile')) {
        localStorage.setItem('tension_relationship_profile', JSON.stringify(relationshipProfile));
      }

      const existingMode = localStorage.getItem('tension_mode');
      const hasCompletedOnboarding = isOnboardingComplete();

      if (hasCompletedOnboarding) {
        navigate('/home', { replace: true });
      } else if (existingMode) {
        navigate(existingMode === 'single' ? '/singles-onboarding' : '/onboarding', { replace: true });
      } else {
        navigate('/mode-selection', { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, syncMfaFromSession]);

  return (
    <WarmMarketingBackdrop>
      <div className="flex min-h-screen items-center justify-center px-6">
        <div className="space-y-4 text-center">
          <div className="mx-auto h-16 w-16 animate-spin rounded-full border-4 border-violet-200 border-t-violet-600" />
          <p className="text-neutral-600">Completing sign in…</p>
        </div>
      </div>
    </WarmMarketingBackdrop>
  );
}
